import * as React from "react";
import { checkSession, login as apiLogin, logout as apiLogout, type AuthIdentity } from "@/lib/authClient";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  status: AuthStatus;
  identity: AuthIdentity | null;
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthStatus>("loading");
  const [identity, setIdentity] = React.useState<AuthIdentity | null>(null);

  const refresh = React.useCallback(async () => {
    const session = await checkSession();
    setIdentity(session);
    setStatus(session ? "authenticated" : "unauthenticated");
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const login = React.useCallback(async (email: string, password: string) => {
    const result = await apiLogin(email, password);
    if (result.ok) {
      setIdentity(result.identity);
      setStatus("authenticated");
      return { ok: true as const };
    }
    return result;
  }, []);

  const logout = React.useCallback(async () => {
    await apiLogout();
    setIdentity(null);
    setStatus("unauthenticated");
  }, []);

  const value = React.useMemo(
    () => ({ status, identity, login, logout, refresh }),
    [status, identity, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
